import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, cx, fieldClass, filterLabelClass, popoverClass } from './Primitives';
import { IconChevronDown, IconSearch } from './icons';
import { useModal } from './useModal';
import { useIsPhone } from './usePhone';

/* -------------------------------------------------------------------------- */
/* Several choices in one filter (DESIGN-SYSTEM §7.4)                           */
/* -------------------------------------------------------------------------- */

export interface MultiOption {
    value: string;
    label: string;
    /** Groups the list, in the order the options arrive ("Next follow-up", "Promise"). */
    group?: string;
    /** How many accounts the choice holds, when the caller knows it. */
    count?: number;
}

/**
 * A filter that takes one value or several.
 *
 * It looks and sits exactly like the filter bar's selects — a label above, a
 * field-shaped button — and opens a small panel of checkboxes beside it (a
 * sheet on a phone). Nothing is applied behind the person's back: each tick
 * changes the filter at once, as a select would, and the panel says how many
 * are chosen. "Any" is no ticks. A long list gets a search box.
 *
 * The panel is drawn outside the page's flow, so a card with hidden overflow
 * cannot clip it, and follows the dialog rules: focus inside while open, Esc
 * or a press outside closes, focus returns to the button.
 */
export function MultiSelect({
    id, label, options, value, onChange, anyLabel = 'Any', active, className,
}: {
    id: string;
    label: string;
    options: readonly MultiOption[];
    value: readonly string[];
    onChange: (values: string[]) => void;
    anyLabel?: string;
    /** Marks the control as narrowing the list; defaults to "anything chosen". */
    active?: boolean;
    className?: string;
}) {
    const [open, setOpen] = useState(false);
    const trigger = useRef<HTMLButtonElement>(null);
    const chosen = new Set(value);
    const known = options.filter(o => chosen.has(o.value));
    const summary = known.length === 0
        ? anyLabel
        : known.length === 1
            ? known[0]!.label
            : `${known[0]!.label} + ${known.length - 1} more`;
    const isActive = active ?? value.length > 0;

    return (
        <div className={className}>
            <label htmlFor={id} className={filterLabelClass}>{label}</label>
            <button
                ref={trigger}
                id={id}
                type="button"
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={() => setOpen(o => !o)}
                className={fieldClass({
                    className: cx('w-full flex items-center gap-2 text-left min-w-36 max-w-64', isActive && '!border-accent bg-accent-tint'),
                })}
            >
                <span className="truncate flex-1">{summary}</span>
                {known.length > 1 && <span className="sr-only">, {known.length} chosen</span>}
                <span className="text-label-3 flex-none" aria-hidden="true"><IconChevronDown size={14} /></span>
            </button>
            {open && trigger.current && (
                <ChoicePanel
                    anchor={trigger.current}
                    label={label}
                    options={options}
                    chosen={chosen}
                    onToggle={(v, on) => {
                        const next = new Set(chosen);
                        if (on) next.add(v); else next.delete(v);
                        onChange(options.map(o => o.value).filter(x => next.has(x)));
                    }}
                    onClear={() => onChange([])}
                    onClose={() => { setOpen(false); window.setTimeout(() => trigger.current?.focus(), 0); }}
                />
            )}
        </div>
    );
}

function ChoicePanel({
    anchor, label, options, chosen, onToggle, onClear, onClose,
}: {
    anchor: HTMLElement;
    label: string;
    options: readonly MultiOption[];
    chosen: Set<string>;
    onToggle: (value: string, on: boolean) => void;
    onClear: () => void;
    onClose: () => void;
}) {
    const isPhone = useIsPhone();
    const panel = useModal<HTMLDivElement>(true, onClose, { closeOnEscape: true });
    const titleId = useId();
    const [query, setQuery] = useState('');
    const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
    const searchable = options.length > 10;

    useLayoutEffect(() => {
        if (isPhone) return;
        const measure = () => {
            const box = anchor.getBoundingClientRect();
            const height = panel.current?.offsetHeight ?? 320;
            const width = panel.current?.offsetWidth ?? 256;
            const below = window.innerHeight - box.bottom >= height + 8 || box.top < height + 8;
            setPlace({
                top: below ? box.bottom + 4 : box.top - height - 4,
                left: Math.min(Math.max(8, box.left), window.innerWidth - width - 8),
            });
        };
        measure();
        window.addEventListener('scroll', measure, true);
        window.addEventListener('resize', measure);
        return () => {
            window.removeEventListener('scroll', measure, true);
            window.removeEventListener('resize', measure);
        };
    }, [anchor, isPhone, panel]);

    useEffect(() => {
        const onDown = (e: PointerEvent) => {
            const t = e.target as Node;
            if (!panel.current?.contains(t) && !anchor.contains(t)) onClose();
        };
        document.addEventListener('pointerdown', onDown);
        return () => document.removeEventListener('pointerdown', onDown);
    }, [anchor, onClose, panel]);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;
    }, [options, query]);

    const groups: { name: string; items: MultiOption[] }[] = [];
    for (const option of visible) {
        const name = option.group ?? '';
        const last = groups[groups.length - 1];
        if (last && last.name === name) last.items.push(option); else groups.push({ name, items: [option] });
    }

    const host = anchor.closest<HTMLElement>('[role="dialog"], [role="alertdialog"]') ?? document.body;

    return createPortal(
        <>
            {isPhone && <div className="fixed inset-0 z-70 bg-overlay animate-fade" aria-hidden="true" />}
            <div
                ref={panel}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                style={isPhone ? undefined : { top: place?.top ?? -9999, left: place?.left ?? -9999 }}
                className={cx(
                    'z-70 flex flex-col',
                    isPhone
                        ? 'fixed inset-x-0 bottom-0 bg-card border-t border-separator shadow-e3 rounded-t-dialog max-h-[80dvh] pb-[env(safe-area-inset-bottom)] animate-sheet'
                        : cx('fixed w-64 max-h-96', popoverClass),
                )}
            >
                <div className="px-3 pt-3 pb-2 flex items-center justify-between gap-2">
                    <p id={titleId} className="text-secondary font-semibold text-label">{label}</p>
                    <span className="text-caption text-label-3 num">{chosen.size === 0 ? 'Any' : `${chosen.size} chosen`}</span>
                </div>
                {searchable && (
                    <div className="px-3 pb-2">
                        <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-label-3 pointer-events-none" aria-hidden="true"><IconSearch size={14} /></span>
                            <input
                                type="search"
                                value={query}
                                data-autofocus
                                aria-label={`Find ${label.toLowerCase()}`}
                                placeholder="Find"
                                onChange={e => setQuery(e.target.value)}
                                className={fieldClass({ className: 'w-full pl-8' })}
                            />
                        </div>
                    </div>
                )}
                <div className="overflow-y-auto px-1.5 pb-1.5 min-h-0">
                    {groups.length === 0 && <p className="px-2 py-3 text-secondary text-label-3">Nothing matches.</p>}
                    {groups.map(group => (
                        <fieldset key={group.name || 'all'} className="py-1">
                            {group.name
                                ? <legend className="px-2 pt-1 pb-0.5 text-caption font-semibold text-label-3">{group.name}</legend>
                                : <legend className="sr-only">{label}</legend>}
                            {group.items.map(option => (
                                <label key={option.value} className="flex items-center gap-2.5 min-h-9 px-2.5 rounded-sm hover:bg-hover cursor-pointer text-body text-label">
                                    <input
                                        type="checkbox"
                                        checked={chosen.has(option.value)}
                                        onChange={e => onToggle(option.value, e.target.checked)}
                                    />
                                    <span className="truncate flex-1">{option.label}</span>
                                    {option.count !== undefined && <span className="text-caption text-label-3 num">{option.count}</span>}
                                </label>
                            ))}
                        </fieldset>
                    ))}
                </div>
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-separator bg-card-2 rounded-b-card">
                    <Button type="button" size="sm" variant="quiet" disabled={chosen.size === 0} onClick={onClear}>Any</Button>
                    <Button type="button" size="sm" variant="secondary" onClick={onClose}>Done</Button>
                </div>
            </div>
        </>,
        host,
    );
}

/* -------------------------------------------------------------------------- */
/* Selection over a list (DESIGN-SYSTEM §9.1)                                  */
/* -------------------------------------------------------------------------- */

/**
 * The bar a list shows once rows are chosen: how many, a way to widen the
 * choice to everything the filters match, a way to let go, and the actions.
 *
 * It sits at the top of the list's own surface, above the rows it acts on, in
 * the page's flow — it never floats over a row, so nothing it could cover
 * becomes hard to reach, and on a phone it does not stack a second bar over
 * the bottom navigation. A polite live region says the count.
 */
export function SelectionBar({
    count, noun, total, onSelectAll, selectAllLabel, onClear, children,
}: {
    count: number;
    /** "account" / "cheque" — the bar adds the plural. */
    noun: string;
    /** Everything the filters match, when more than are chosen can be chosen. */
    total?: number;
    onSelectAll?: () => void;
    selectAllLabel?: string;
    onClear: () => void;
    children: React.ReactNode;
}) {
    const plural = (n: number) => `${n} ${n === 1 ? noun : `${noun}s`}`;
    return (
        <div
            role="region"
            aria-label="Chosen"
            className="border-b border-separator bg-accent-tint px-4 py-2 flex items-center gap-x-3 gap-y-2 flex-wrap"
        >
            <p className="text-secondary font-semibold text-label" aria-live="polite">{plural(count)} chosen</p>
            {onSelectAll && total !== undefined && total > count && (
                <Button type="button" size="sm" variant="quiet" className="!text-accent-text" onClick={onSelectAll}>
                    {selectAllLabel ?? `Choose all ${total}`}
                </Button>
            )}
            <Button type="button" size="sm" variant="quiet" onClick={onClear}>Clear</Button>
            <div className="flex items-center gap-2 flex-wrap ml-auto">{children}</div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Proportions (DESIGN-SYSTEM §9.2)                                            */
/* -------------------------------------------------------------------------- */

/**
 * One proportion as a bar: a share of a whole that the numbers beside it
 * already state. Never a gauge, never decoration — it is drawn only where the
 * figure *is* a proportion, and its spoken label says the figures.
 */
export function ProportionBar({
    share, label, tone = 'accent', className,
}: {
    /** 0 to 1. */
    share: number;
    /** What a screen reader hears: the figures, in words. */
    label: string;
    tone?: 'accent' | 'pos' | 'warn' | 'dang';
    className?: string;
}) {
    const width = `${Math.round(Math.min(1, Math.max(0, share)) * 1000) / 10}%`;
    // Fills are signal colours: jade for good, amber for attention, crimson for wrong — never the age ramp, which is navy.
    const fill = { accent: 'bg-accent', pos: 'bg-mark', warn: 'bg-signal-warn', dang: 'bg-signal-dang' }[tone];
    return (
        <div role="img" aria-label={label} className={cx('h-2 rounded-full bg-card-3 overflow-hidden', className)}>
            <div className={cx('h-full rounded-full', fill)} style={{ width }} />
        </div>
    );
}

/**
 * The ageing bands as one bar, oldest on the right — how the whole book is
 * split, at a glance, beside the table that has the exact amounts. The four
 * colours are the ageing tokens and nothing else uses them.
 */
/**
 * The age colour of part `i` of `n`, youngest first: the youngest is always
 * the first colour and the oldest always the last, those between spread along
 * the scale. With four parts it is the four colours in order; a company's own
 * windows (two to eight) keep the same meaning.
 */
export const ageSwatch = (i: number, n: number): string =>
    // The navy age ramp (Customer 360, F2): an age is not overdue, so no band of it is drawn in a warning colour.
    (['bg-ageing-1', 'bg-ageing-2', 'bg-ageing-3', 'bg-ageing-4'] as const)[n <= 1 ? 0 : Math.round((i * 3) / (n - 1))]!;

export function AgeingBar({
    parts, label, className,
}: {
    /** The amounts, youngest first: a sheet's four bands, or a company's own windows. Negative or missing parts count as none. */
    parts: readonly number[];
    label: string;
    className?: string;
}) {
    const clean = parts.map(p => (Number.isFinite(p) && p > 0 ? p : 0));
    const total = clean.reduce((a, b) => a + b, 0);
    return (
        <div role="img" aria-label={label} className={cx('h-3 rounded-full overflow-hidden flex gap-0.5', total > 0 ? '' : 'bg-card-3', className)}>
            {total > 0 && clean.map((part, i) => part > 0 && (
                <div key={i} className={cx('h-full', ageSwatch(i, clean.length))} style={{ width: `${(part / total) * 100}%` }} />
            ))}
        </div>
    );
}

/** The colour of lateness band `i` (1–30, 31–60, 61–90, over 90 days past due): crimson, later is deeper. */
export const lateSwatch = (i: number): string =>
    (['bg-late-1', 'bg-late-2', 'bg-late-3', 'bg-late-4'] as const)[Math.min(3, Math.max(0, i))]!;

/**
 * Parts of one whole as one bar (Customer 360, 02 §9.2): each part its own fill, the surface between them, rounded only
 * at the bar's two ends — what is overdue beside what is not yet due, how late the overdue is, how promises ended. The
 * exact figures are always in the words beside it; the bar is never the only place a figure is.
 */
export function SegmentBar({ parts, label, className }: {
    parts: readonly { amount: number; fill: string; key?: string }[];
    /** What a screen reader hears: the figures, in words. */
    label: string;
    className?: string;
}) {
    const shown = parts.filter(p => Number.isFinite(p.amount) && p.amount > 0);
    const total = shown.reduce((s, p) => s + p.amount, 0);
    return (
        <div role="img" aria-label={label} className={cx('h-2.5 rounded-full overflow-hidden flex gap-0.5', total > 0 ? '' : 'bg-card-3', className)}>
            {total > 0 && shown.map((p, i) => (
                <div key={p.key ?? i} className={cx('h-full min-w-0', p.fill)} style={{ flex: `${p.amount / total} 1 0%` }} />
            ))}
        </div>
    );
}

/** A key, as a shortcut hint shows it. */
export const Kbd = ({ children, className }: { children: React.ReactNode; className?: string }) => (
    <kbd className={cx('inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-sm border text-caption font-medium font-sans', className ?? 'border-separator-strong bg-card-2 text-label-2')}>
        {children}
    </kbd>
);
